'use client';

import { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import orLeadGenFormVariants from './or-lead-gen-form.variants';
import * as yup from 'yup';

// components
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtText } from '@/components/atoms/at-text/at-text';
import { MlForm } from '@/components/molecules/ml-form/ml-form';
import { RenderRichText } from '@/components/helpers/cms/RenderRichText';

// types
import {
  IOrleadGenFormProps,
  EFormType,
  IFormStep,
  IEnrichedData,
} from './or-lead-gen-form.types';
import { IDesignStudio } from '../or-design-studio-block/or-design-studio-block.types';
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';
import { ISelectOption } from '@/components/atoms/at-dropdown-field/at-dropdown-field.types';

// utils + hooks
import { useCookiesNext } from 'cookies-next';
import { usePathname } from 'next/navigation';
import { useGlobalLinks } from '@/context/globalLinksContext';
import {
  sendGTMFormErrorEvent,
  sendGTMFormSubmitEvent,
} from '@/components/helpers/analytics/gtm';
import { sha256 } from 'js-sha256';
import {
  cleanUrlPath,
  identifyHubspotLead,
} from '@/components/helpers/integrations/hubspot';
import { useLocation } from '@/context/locationContext';

// constants
import {
  contactUsFormFields,
  formSchema,
  singleStepFormFields,
  twoStepFormFieldsStepOne,
  twoStepFormFieldsStepTwo,
} from './constants';
import { AtMedia } from '@/components/atoms';
import { IImageGlobalCSProps } from '@/lib/contentstack-sdk/types';
import { EImageIntents } from '@/components/atoms/at-media/at-media.types';
import useBreakpoint from '@/hooks/useBreakpoint';
import { EAlignment } from '@/components/molecules/ml-header/ml-header.types';

const LEAD_GEN_FORM_COOKIE = 'isLeadGenFormSubmitted';

export function hasImage(
  background_image?: IImageGlobalCSProps | null,
): boolean {
  if (!background_image || typeof background_image !== 'object') {
    return false;
  }

  const mobile = background_image.mobile_image_url;

  return !!(
    mobile &&
    typeof mobile === 'object' &&
    'uid' in mobile &&
    typeof mobile.uid === 'string' &&
    mobile.uid.trim() !== ''
  );
}

export const OrLeadGenForm = (props: IOrleadGenFormProps): JSX.Element => {
  const {
    formId,
    formName,
    heading,
    headingElement,
    headingVariant,
    description,
    formType,
    isInModal = false,
    topSpacing = 'md',
    bottomSpacing = 'md',
    topDivider = false,
    bottomDivider = false,
    contactPageTypeUrl = null,
    backgroundImage = null,
    alignment = 'left',
  } = props;

  const includesImage = hasImage(backgroundImage);
  const shouldStack = useMemo(
    () => includesImage || isInModal,
    [includesImage, isInModal],
  );

  const currentAlignment =
    isInModal || !includesImage
      ? EAlignment.CENTER
      : EAlignment[alignment.toUpperCase() as keyof typeof EAlignment];
  const { allStudios, currentBuildStudio } = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasSubmitFailed, setHasSubmitFailed] = useState(false);

  const classes = orLeadGenFormVariants({
    topSpacing,
    bottomSpacing,
  });
  const { getCookie, setCookie } = useCookiesNext();

  const { globalLinks } = useGlobalLinks();

  useEffect(() => {
    const isFormSubmitted = getCookie(LEAD_GEN_FORM_COOKIE);

    if (formType === EFormType.DOWNLOAD && isFormSubmitted) {
      setIsSubmitted(true);
    }
  }, [getCookie, formType]);

  const buildLocationOptions = useMemo((): Array<ISelectOption> => {
    const sortedStudios = [...allStudios].sort((a, b) => {
      const stateCompare = a.State.localeCompare(b.State);
      if (stateCompare !== 0) return stateCompare;
      return a.Name.localeCompare(b.Name);
    });

    return sortedStudios.map((studio: IDesignStudio) => ({
      label: `${studio?.CsState || studio.State}, ${studio?.CsName || studio?.Name.replace(/\//g, '-')}`,
      value: `${studio.Id},${studio.State},${studio?.Name.replace(/\\\//g, '/')}`,
    }));
  }, [allStudios]);

  const userLocationOption = buildLocationOptions.find((loc) =>
    loc.value.startsWith(`${currentBuildStudio?.Id},`),
  );

  const updatedBuildLocationOptions: ISelectOption[] = [
    { label: 'Select', value: '' }, // Empty value for default selection
    ...buildLocationOptions,
  ];

  const formSteps = useMemo((): Array<IFormStep> => {
    switch (formType) {
      case EFormType.SINGLE_STEP:
        return [
          {
            key: `step-1`,
            formFields: singleStepFormFields(updatedBuildLocationOptions),
            stepIndex: 0,
            submitToHubspot: true,
          },
        ];

      case EFormType.TWO_STEP:
        return [
          {
            key: `step-1`,
            formFields: twoStepFormFieldsStepOne(updatedBuildLocationOptions),
            stepIndex: 0,
          },
          {
            key: `step-2`,
            formFields: twoStepFormFieldsStepTwo,
            stepIndex: 1,
            submitToHubspot: true,
          },
        ];

      case EFormType.DOWNLOAD:
        return [
          {
            key: `step-1`,
            formFields: singleStepFormFields(updatedBuildLocationOptions),
            stepIndex: 0,
            submitToHubspot: true,
          },
        ];

      case EFormType.CONTACT_US:
        return [
          {
            key: `step-1`,
            formFields: contactUsFormFields(updatedBuildLocationOptions),
            stepIndex: 0,
            submitToHubspot: true,
          },
        ];

      case EFormType.CONTACT_US:
        return [
          {
            key: `step-1`,
            formFields: contactUsFormFields(buildLocationOptions),
            stepIndex: 0,
            submitToHubspot: true,
          },
        ];

      default:
        return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, updatedBuildLocationOptions]);

  const { isLastStep } = useMemo(() => {
    return { isLastStep: currentStep === formSteps?.length - 1 };
  }, [currentStep, formSteps?.length]);

  const handleNext = useCallback(() => {
    if (currentStep < formSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, formSteps]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const pathname = usePathname();
  const handleSubmit = async (data: yup.InferType<typeof formSchema>) => {
    if (hasSubmitFailed) {
      setHasSubmitFailed(false);
    }

    const gaId = getCookie('_ga');

    const gclid = getCookie('gclid');
    const gclidParsed = gclid ? JSON.parse(gclid) : {};

    const msclkid = getCookie('msclkid');
    const msclkidParsed = msclkid ? JSON.parse(msclkid) : {};

    const utmRaw = getCookie('utm');
    const utm = utmRaw ? JSON.parse(utmRaw) : {};

    const epik = getCookie('epik');
    const fbclid = getCookie('fbclid');
    const tbclid = getCookie('tbclid');

    const gaParsedId = gaId
      ? gaId.toString().replace(/^GA\d+\.\d+\./g, '')
      : gaId?.toString();

    const payload: IEnrichedData = data;
    if (contactPageTypeUrl) {
      payload.contactPageTypeUrl = contactPageTypeUrl;
    }

    const enrichedPayload = {
      ...payload,
      tracking: {
        gclid: gclidParsed?.gclid,
        msclkid: msclkidParsed?.msclkid,
        gaId: gaParsedId,
        epik: epik,
        fbclid: fbclid,
        tbclid: tbclid,
        ...utm,
      },
    };

    const response = await fetch('/submit-form', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(enrichedPayload),
    });

    if (!response.ok) {
      setHasSubmitFailed(true);

      const responseBody = await response.json();

      // Looking for validation errors in case of Bad Request (400)
      if (response.status === 400 && responseBody.errors) {
        const entries = Object.entries(responseBody.errors);
        entries.forEach((entry) => {
          const [key, value] = entry;
          sendGTMFormErrorEvent({
            form_name: formName,
            form_field: key,
            error_message: value as string,
          });
        });
      }
      return;
    }

    setIsSubmitted(true);

    setCookie(LEAD_GEN_FORM_COOKIE, true);

    const {
      email,
      phone,
      buildLocation,
      isLotOwner,
      marketingConsent,
      moveInDate,
    } = data;

    const hashedEmail = sha256(email);
    const hashedPhone = sha256(phone);
    const locationData = buildLocation?.split(',');

    sendGTMFormSubmitEvent({
      form_name: formName,
      form_id: formId,
      step_no: currentStep + 1,
      closest_building_area: locationData ? locationData[2] : '',
      lot_ownership: isLotOwner || '',
      hashed_email: hashedEmail,
      hashed_phone: hashedPhone,
      marketing_opt_in: !!marketingConsent,
      move_in_date: moveInDate || '',
      contact_page_type_url: cleanUrlPath(pathname ?? ''),
    });

    identifyHubspotLead(email);

    if (!isLastStep) {
      handleNext();
    }
  };

  const progressBarClass = useMemo(() => {
    const progressBarWidth = ((currentStep + 1) / formSteps.length) * 100;
    if (progressBarWidth === 50) {
      return `w-1/2 bg-progressBar-active`;
    }
    return 'w-full bg-progressBar-active';
  }, [currentStep, formSteps]);

  const guidebookUrl = globalLinks.filter((url) =>
    url.title.toLowerCase().includes('guidebook'),
  );
  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint === 'lg' || breakpoint === 'xl';
  return (
    <div
      data-theme="light"
      className={classNames('min-h-auto relative flex h-full flex-col', {
        'lg:min-h-screen': !isInModal,
      })}
    >
      {includesImage &&
        backgroundImage?.mobile_image_url?.url &&
        !isInModal && (
          <AtMedia
            src={{
              mobile: backgroundImage.mobile_image_url.url,
              tablet: backgroundImage.tablet_image_url?.url || '',
              desktop: backgroundImage.desktop_image_url?.url || '',
            }}
            sizes="100vw"
            alt="Background"
            roundedCorners="none"
            intent={
              isDesktop ? EImageIntents.BACKGROUND : EImageIntents.DEFAULT
            }
            decorative
            quality={Number(backgroundImage?.quality) || 75}
            priority={backgroundImage?.priority || false}
            blurTheme="neutral"
            className=""
            figureClasses="aspect-video lg:!absolute lg:aspect-none"
            preload={true}
          />
        )}
      <div
        className={classNames('relative lg:items-center', classes, {
          'md:container lg:container lg:min-h-screen': !isInModal,
          'lg:flex lg:justify-center':
            shouldStack && currentAlignment === 'center',
          'lg:flex lg:justify-start':
            shouldStack && currentAlignment === 'left',
          'lg:flex lg:justify-end': shouldStack && currentAlignment === 'right',
          block: !shouldStack,
        })}
      >
        {topDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
          />
        )}

        <section
          className={classNames(
            'relative z-10 flex h-fit flex-col rounded-15 md:justify-between',
            {
              'flex-col gap-8 bg-white md:flex-col lg:max-w-3xl': shouldStack,
              'max-w-3xl': isInModal,
              'bottom-20 mb-[-2rem] px-6 py-8 md:bottom-[9.375rem] md:mb-[-9.375rem] lg:bottom-0 lg:mb-0 lg:px-12 lg:py-15':
                includesImage,
              'p-2 pt-6': !includesImage,
              'flex-col md:flex-row': !isInModal,
            },
          )}
        >
          <div
            className={classNames('z-10 md:basis-1/3 md:pr-8 xl:pr-0', {
              'mb-6': isInModal,
              'mb-6 md:mb-0': !isInModal,
            })}
          >
            <AtHeader
              element={headingElement || 'h2'}
              variant={headingVariant || 'h2'}
              className="mb-4"
            >
              {heading}
            </AtHeader>
            {description && (
              <RenderRichText
                body={description}
                className="text-base text-skin-tertiary md:text-lg [&>*]:text-base md:[&>*]:text-lg"
              />
            )}
          </div>
          <div className="md:basis-2/3 xl:basis-1/2">
            {!isSubmitted && formSteps.length > 1 && (
              <div className="mb-8 md:mb-10">
                <AtText size="sm">
                  STEP {currentStep + 1} OF {formSteps.length}
                </AtText>
                <div className="mt-2 h-2 w-full rounded-full bg-progressBar-inactive">
                  <div
                    className={classNames(`h-2 rounded-full`, progressBarClass)}
                  ></div>
                </div>
              </div>
            )}
            <MlForm
              isInModal={isInModal}
              formId={formId}
              formName={formName}
              formSteps={formSteps}
              formType={formType}
              formSchema={formSchema}
              currentStep={currentStep}
              isLastStep={isLastStep}
              isSubmitted={isSubmitted}
              showError={hasSubmitFailed}
              handleNext={handleNext}
              handlePrevious={handlePrevious}
              handleSubmit={handleSubmit}
              returnUrl={guidebookUrl[0].custom_url}
              userLocationOption={userLocationOption}
            />
          </div>
        </section>
        {bottomDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
          />
        )}
      </div>
    </div>
  );
};
